/**
 * VRG-610 — PRODUCT CATEGORIES, and the map that resolves a PRODUCT TYPE up to one.
 *
 * ── WHY THIS FILE IS IN @repo/types AND NOT IN apps/platform ──────────────
 *
 * `PRODUCT_CATEGORIES` lived in `apps/platform/src/constants/categories.ts` and is
 * hoisted here for the same reason VRG-573 hoisted `ALL_PRODUCT_TYPES` out of the same
 * directory: a SECOND app now needs the list, and the alternative to hoisting is a
 * second copy.
 *
 * The second app is `apps/api`. `CategoryMapping.veraCategory` in
 * `apps/api/src/utils/category-mapper.ts` was typed `string`, so writing
 * `veraCategory: 'cannabis'` compiled clean whether or not `cannabis` was a category
 * the platform could display or filter to — and the API auto-assigns that value to
 * imported products. A closed set is the only thing that makes that a compile error,
 * and `apps/api` cannot import `apps/platform` (app → app is not a dependency edge
 * this monorepo has; layer 1 is `packages/*`).
 *
 * The map below needs both lists anyway — its domain is `ALL_PRODUCT_TYPES` (this
 * directory) and its codomain is `PRODUCT_CATEGORIES` — so once the list is here the
 * map belongs beside it, under ONE module-load guard rather than a guard in each app
 * that can only see half of what it is checking.
 *
 * `apps/platform/src/constants/categories.ts` now re-exports from here, so its call
 * sites are untouched and there is still exactly one definition.
 *
 * ── ⚠️ THE TWO LISTS ARE DIFFERENT GRAINS, NOT TWO SPELLINGS ─────────────
 *   PRODUCT_CATEGORIES (here, 23)        what a COMPANY sells. Stored on
 *                                        companies.categories.
 *   ALL_PRODUCT_TYPES      (here, 68)        what a TAG is best for. Drives best_for_product_types
 *                                        and search.
 *
 * A product type usually falls under one category, but the relationship is
 * many-to-many rather than a strict hierarchy. Gemstones and Jewelry are two
 * categories here that the website groups as one industry; the website's
 * "Cosmetics & Perfume" industry is this list's single `perfume`. That divergence is
 * FINE and expected — the website is deliberately higher-level, and may never have a
 * page per category.
 */
import {ALL_PRODUCT_TYPES} from './product-types.js';

export const PRODUCT_CATEGORIES = [
  'apparel',
  'art',
  'automotive',
  'books',
  'cannabis',
  'caviar',
  'collectibles',
  'event_tickets',
  'eyewear',
  'footwear',
  'gemstones',
  'handbags',
  'jewelry',
  /* ⚠️ "Legal Documents", NOT "Documents" — narrower on purpose (Spencer,
     2026-09-18), and it will get its own website industry page.

     ⚠️ NOTE THE INVERSION THIS CREATES. `Documents` exists as a PRODUCT TYPE in
     ALL_PRODUCT_TYPES and is BROADER than this category — a category is normally the
     coarser of the two. See PRODUCT_TYPE_CATEGORIES below for how the map resolves
     it and why. Do not resolve it by widening this back. */
  'legal_documents',
  'membership_cards',
  'music',
  'other',
  'perfume',
  'pharmaceuticals',
  'supplements',
  'trading_cards',
  'watches',
  'wine_spirits',
] as const;

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

/**
 * Display names for categories (title case with proper formatting).
 *
 * Lives beside the list rather than in the platform so the two cannot be edited in
 * different packages and drift; the guard at the bottom asserts they agree.
 */
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  apparel: 'Apparel',
  art: 'Art',
  automotive: 'Automotive',
  books: 'Books',
  cannabis: 'Cannabis',
  caviar: 'Caviar',
  collectibles: 'Collectibles',
  legal_documents: 'Legal Documents',
  event_tickets: 'Event Tickets',
  eyewear: 'Eyewear',
  footwear: 'Footwear',
  gemstones: 'Gemstones',
  handbags: 'Handbags',
  jewelry: 'Jewelry',
  membership_cards: 'Membership Cards',
  music: 'Music',
  other: 'Other',
  perfume: 'Perfume',
  pharmaceuticals: 'Pharmaceuticals',
  supplements: 'Supplements',
  trading_cards: 'Trading Cards',
  watches: 'Watches',
  wine_spirits: 'Wine & Spirits',
};

/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCT TYPE → PRODUCT CATEGORY (VRG-610)

   ── WHAT THIS IS FOR, AND WHY IT IS NOT A READABILITY FIX ─────────────────

   A tag product is a FORM (Sticker, Solid, Closure …) sold as a ladder of
   capabilities across a set of sizes. Its form-level `best_for_product_types` is therefore a
   UNION across every size it offers — and a union OVER-CLAIMS at product-type
   grain. "Sticker Tag is best for Watches" is only true at 28mm. The same union
   is largely HONEST at category grain, because "Sticker Tag is best for Apparel"
   holds at 10mm and at 45mm alike.

   Moving the shop grid's display to category grain is what makes the union a TRUE
   statement. Shortening a 31-item list to a 13-item one is a side effect, not the
   reason. The three grains, per TAGS.md §5 "Best-for, settled":

       Shop grid, form level    -> PRODUCT CATEGORY  (coarse, display)
       Product page, size set   -> PRODUCT TYPE      (a narrowing, not a new claim)
       Search                   -> product type resolves UP to category

   The product-type `best_for_product_types` is untouched and remains the search index.

   ── MANY-TO-MANY IS PERMITTED AND IS REAL ────────────────────────────────
   Most entries are 1:1. `Ticketing & Access` is genuinely two categories: a ticket
   is `event_tickets`, an access credential is `membership_cards`. Collapsing it to
   either would drop half the claim.

   ── ⚠️ `Documents` INVERTS THE GRAIN, AND IS MAPPED DELIBERATELY ──────────
   Categories are normally the COARSER list. `Documents` is the exception: the
   product type covers any paper being authenticated (certificates, deeds,
   provenance letters) while the category `legal_documents` is deliberately
   narrower (Spencer, 2026-09-18) and will get its own website industry page.

   It maps to `legal_documents` anyway, for two reasons and against two rejected
   alternatives:

     · DIRECTION OF ERROR. Resolving a broader type onto a narrower category makes
       the rendered line "Card is best for Legal Documents" — true of a Card, just
       not the whole truth. That is an UNDER-claim. This ticket exists to stop
       OVER-claiming; trading an over-claim for an under-claim is the trade it is
       asking for, and the opposite trade is the one it forbids.
     · THE ALTERNATIVE IS WORSE. Leaving `Documents` unmapped deletes it from
       Sticker's and Card's display lines entirely and makes a search for a deed
       return nothing — the silent-gap failure the guard below exists to prevent.

   REJECTED: widening the category back to "Documents" (explicitly ruled out where
   `legal_documents` is declared above). REJECTED: mapping it to
   `[legal_documents, other]`, which adds a category that says nothing and would
   make `other` appear on the two forms that carry `Documents` for no gain.

   ── ⚠️ `other` IS A REAL TARGET, AND IS DROPPED FROM DISPLAY ─────────────
   23 categories cannot cover 68 product types. Furniture, Lighting, Firearms,
   Electronics, Sporting Goods and ~20 others have no category and never will —
   the category list is built around the goods Vera sells INTO, not around
   everything a tag can stick to.

   `other` is the honest answer for those, and it keeps the map TOTAL, which is
   what lets the guard below demand total coverage. But `other` is not a category
   anyone browses by, so `bestForCategoriesForForm` drops it from the DISPLAY
   projection while search keeps it. The split is deliberate: display must be
   meaningful, resolution must be total, and those are different requirements.
   ═══════════════════════════════════════════════════════════════════════════ */
export const PRODUCT_TYPE_CATEGORIES: Record<string, ProductCategory[]> = {
  // ── Fashion & Apparel ──────────────────────────────────────────────────
  'Apparel & Clothing': ['apparel'],
  Sneakers: ['footwear'],
  Footwear: ['footwear'],
  Handbags: ['handbags'],
  /* Wallets and Luggage follow Shopify's own grouping, `aa-5 Apparel &
     Accessories > Handbags, Wallets & Cases`, which category-mapper.ts already
     collapses wholesale onto `handbags`. Agreeing with the importer beats
     inventing a second opinion the importer cannot act on. */
  Wallets: ['handbags'],
  Luggage: ['handbags'],
  Eyewear: ['eyewear'],
  // Clothing accessories. `apparel` is the category the importer files aa-2 under.
  Belts: ['apparel'],
  Scarves: ['apparel'],
  Hats: ['apparel'],

  // ── Jewelry, Watches & Precious ────────────────────────────────────────
  Jewelry: ['jewelry'],
  Watches: ['watches'],
  Gemstones: ['gemstones'],
  /* Bullion is an investment asset, not an ornament, so `jewelry` is wrong.
     `collectibles` is where its nearest sibling `Coins` lands and where Shopify
     files collectible coins and currency (ae-2-2). */
  Bullion: ['collectibles'],

  // ── Beauty & Personal Care ─────────────────────────────────────────────
  /* All three sit under Shopify's `hb-3-2 Cosmetics`, and the platform has ONE
     category for that whole branch. The website's industry is even labelled
     "Cosmetics & Perfume" against this slug — see the label/slug trap in
     TAGS.md §5. */
  'Fragrance & Perfume': ['perfume'],
  Cosmetics: ['perfume'],
  Skincare: ['perfume'],
  // Home fragrance, not personal care. No category — see the `other` note above.
  Candles: ['other'],

  // ── Art & Design ───────────────────────────────────────────────────────
  Art: ['art'],
  Sculpture: ['art'],
  Photography: ['art'],

  // ── Collectibles ───────────────────────────────────────────────────────
  /* `trading_cards` is its own category and stays separate from `collectibles`:
     the platform draws that line, and the map does not get to redraw it. */
  'Trading Cards': ['trading_cards'],
  Currency: ['collectibles'],
  Coins: ['collectibles'],
  Stamps: ['collectibles'],
  Comics: ['collectibles'],
  Memorabilia: ['collectibles'],
  Toys: ['collectibles'],
  Collectibles: ['collectibles'],

  // ── Music, Media & Books ───────────────────────────────────────────────
  'Vinyl & Records': ['music'],
  'Musical Instruments': ['music'],
  Books: ['books'],

  // ── Wine, Spirits & Gourmet ────────────────────────────────────────────
  Wine: ['wine_spirits'],
  Spirits: ['wine_spirits'],
  /* Caviar has its own category; the rest of the food and drink product types do not,
     and `caviar` must NOT absorb them — a jar of honey is not caviar, and a
     category that means "perishable luxury food" is a different category nobody
     has decided to create. */
  'Gourmet Food': ['other'],
  'Coffee & Tea': ['other'],
  'Cigars & Tobacco': ['other'],
  Caviar: ['caviar'],

  // ── Automotive & Transport ─────────────────────────────────────────────
  /* `automotive` is the category behind Shopify's `vp Vehicles & Parts`, so every
     vehicle product type lands there. Bicycles is the arguable one — Shopify files it
     under Sporting Goods — but the Vera category is about VEHICLES and `other` is
     strictly less informative, so it stays here. */
  Automobiles: ['automotive'],
  'Auto Parts': ['automotive'],
  Motorcycles: ['automotive'],
  Bicycles: ['automotive'],
  'Marine & Boats': ['automotive'],

  // ── Electronics & Tech ─────────────────────────────────────────────────
  Electronics: ['other'],
  'Audio Equipment': ['other'],

  // ── Home & Living ──────────────────────────────────────────────────────
  Furniture: ['other'],
  'Home Décor': ['other'],
  /* A rug is decor, not `art`. Mapping it to `art` would put rugs on the Art
     industry page and in an Art search, which is a claim about the goods, not a
     convenience. */
  'Rugs & Carpets': ['other'],
  Lighting: ['other'],

  // ── Sports & Outdoor ───────────────────────────────────────────────────
  'Sporting Goods': ['other'],
  'Golf Equipment': ['other'],
  Camping: ['other'],

  // ── Regulated & Controlled ─────────────────────────────────────────────
  Firearms: ['other'],
  Cannabis: ['cannabis'],
  Pharmaceuticals: ['pharmaceuticals'],
  /* A medical device is not a pharmaceutical — different regulator, different
     product, and merging them would put implants in a Pharmaceuticals search. */
  'Medical Devices': ['other'],
  Supplements: ['supplements'],
  Hazardous: ['other'],

  // ── Industrial & B2B ───────────────────────────────────────────────────
  'Industrial Machinery': ['other'],
  'Aerospace & Defense': ['other'],
  'Electrical Components': ['other'],

  // ── Access & Digital ───────────────────────────────────────────────────
  Memberships: ['membership_cards'],
  /* THE MANY-TO-MANY CASE. A concert ticket is `event_tickets`; a building-access
     pass is `membership_cards`. The product type covers both and the categories
     split them, so it maps to both. */
  'Ticketing & Access': ['event_tickets', 'membership_cards'],
  // See the `Documents` block in the header — this is the deliberate inversion.
  Documents: ['legal_documents'],
  Credentials: ['membership_cards'],

  // ── Luxury Miscellany ──────────────────────────────────────────────────
  'Writing Instruments': ['other'],
  Lighters: ['other'],

  // ── Other ──────────────────────────────────────────────────────────────
  'Other / Not Listed': ['other'],
};

/** Index of a category's position, so results can be returned in list order. */
const CATEGORY_ORDER = new Map<string, number>(PRODUCT_CATEGORIES.map((c, i) => [c, i]));

/**
 * Categories for ONE product type. Total by construction — the guard below refuses
 * to let the module load if any type is unmapped — so this never returns empty for a
 * canonical type. An unknown string returns `[]` rather than throwing, because the
 * callers are search and display paths where a stale stored value should degrade to
 * "no category", not take the page down.
 */
export function categoriesForProductType(productType: string): ProductCategory[] {
  return PRODUCT_TYPE_CATEGORIES[productType] ?? [];
}

/**
 * Resolve a list of product types UP to categories: dedupe, return in
 * PRODUCT_CATEGORIES order.
 *
 * Order is the declared list order rather than first-seen so the result is stable —
 * the same set of types always renders the same string, and a metafield value does
 * not churn because a product type was added in the middle of an affinity list.
 *
 * `includeOther` defaults to TRUE because this is the SEARCH-side resolution and
 * must be total. Display callers pass `false`; see `bestForCategoriesForForm`.
 */
export function categoriesForProductTypes(
  productTypes: readonly string[],
  {includeOther = true}: {includeOther?: boolean} = {},
): ProductCategory[] {
  const found = new Set<string>();
  for (const t of productTypes) for (const c of categoriesForProductType(t)) found.add(c);
  if (!includeOther) found.delete('other');
  return PRODUCT_CATEGORIES.filter(c => found.has(c));
}

/* ═══════════════════════════════════════════════════════════════════════════
   ⚠️ A MODULE-LOAD GUARD, NOT A TEST — and it reports EVERY violation at once.

   ── WHY A THROW ──────────────────────────────────────────────────────────
   Both failures this catches are SILENT, and both read as a data problem rather
   than a bug:

     · An UNMAPPED product type resolves to no category. It then vanishes from
       every display line that would have named it and returns nothing from a
       search — which reads as "we do not cover that" rather than "the map has a
       hole". This is the same failure PRODUCT_TYPE_CODE's guard was written for,
       where a hand-written regex silently produced 62 of 68 and dropped exactly
       the regulated product types.
     · A category that is NOT in PRODUCT_CATEGORIES is a value the platform cannot
       display, cannot filter to and has no display name for. It looks correct in
       source and renders as nothing.

   Neither surfaces at a call site, so there is no natural place for the failure to
   appear. A throw at module load puts it in front of whoever made the edit.

   ── WHY ALL VIOLATIONS, NOT THE FIRST ────────────────────────────────────
   These lists are edited in batches — a sector is added, or a category is
   renamed across the map. Reporting the first offender turns one fix into N
   edit-run cycles and, worse, invites the reader to believe the one named is the
   only one. Both existing guards in this directory (PRODUCT_TYPE_CODE above,
   affinity.ts's vocabulary guard) collect and report in full; this matches them.
   ═══════════════════════════════════════════════════════════════════════════ */
{
  const validCategory = new Set<string>(PRODUCT_CATEGORIES);

  const unmapped = ALL_PRODUCT_TYPES.filter(v => !PRODUCT_TYPE_CATEGORIES[v]?.length);
  const unknownType = Object.keys(PRODUCT_TYPE_CATEGORIES).filter(
    v => !ALL_PRODUCT_TYPES.includes(v),
  );
  const unknownCategory = [
    ...new Set(
      Object.entries(PRODUCT_TYPE_CATEGORIES).flatMap(([type, cats]) =>
        cats.filter(c => !validCategory.has(c)).map(c => `${type} -> ${c}`),
      ),
    ),
  ];
  const noDisplayName = PRODUCT_CATEGORIES.filter(c => !CATEGORY_DISPLAY_NAMES[c]);

  const problems = [
    unmapped.length ? `product type(s) with no category: ${unmapped.join(', ')}` : '',
    unknownType.length
      ? `mapped name(s) that are not product types in ALL_PRODUCT_TYPES: ${unknownType.join(', ')}`
      : '',
    unknownCategory.length
      ? `category value(s) not in PRODUCT_CATEGORIES: ${unknownCategory.join('; ')}`
      : '',
    noDisplayName.length
      ? `category(ies) with no CATEGORY_DISPLAY_NAMES entry: ${noDisplayName.join(', ')}`
      : '',
  ].filter(Boolean);

  if (problems.length) {
    throw new Error(
      `PRODUCT_TYPE_CATEGORIES is out of sync — ${problems.join('; ')}. ` +
        `This map is how a product type resolves up to a displayable category, so a ` +
        `hole in it removes that type from every "best for" line and from search ` +
        `silently, and a category outside PRODUCT_CATEGORIES is one the platform ` +
        `cannot display or filter to.`,
    );
  }
}
